import React, { useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import { useSavePricingPolicy } from '../../hooks/usePricingGuidance';
import { pricingErrorMessage } from '../../utils/pricingErrorCodes';

/**
 * The settings strip — the commercial reference, the advisory floor, and long-stay tiers.
 *
 * The REFERENCE is what the quote is calculated from: the unit's normal commercial
 * position, agreed with the owner or set by us. It is not a published rate and is never
 * synced anywhere. It exists because a published rate is sometimes a channel tactic
 * rather than a statement of value, and a quote built on a tactic is a quote built on an
 * experiment (tasks/pricing-commercial-reference.md).
 *
 * The CALENDAR DEFAULT is the exception on this card: it IS published — but only on the
 * far-future nights the nightly horizon job creates, never on a night that already exists.
 *
 * The FLOOR is ADVISORY: it produces a warning on a quote and never blocks one
 * (decision 10). It must stay that way — a new unit's launch offer deliberately goes
 * below it to buy first reviews.
 *
 * The floor is deliberately NOT shown next to the reference on the quote itself. A floor
 * on screen beside a price teaches ops the lowest number every time they open the page;
 * it surfaces only when an offer actually breaches it.
 *
 * Tiers are user-defined from 2 nights up rather than the fixed 7/14/28 the plan
 * originally carried. The lever that actually fills a midweek is a 3–4 night bridge
 * discount carrying a guest from the weekend into the week, and hard-coded tiers could
 * not express the very mechanism the weekday block tells ops to use (§9b).
 */
const emptyTier = { minNights: '', discountPercent: '' };

const PolicySettings = ({ propertyId, policy }) => {
  const { t, isArabic } = useLanguage();
  const save = useSavePricingPolicy(propertyId);
  const [formError, setFormError] = useState(null);

  const [referenceRate, setReferenceRate] = useState(policy?.referenceRate ?? '');
  const [floor, setFloor] = useState(policy?.floor ?? '');
  const [calendarRate, setCalendarRate] = useState(policy?.calendarRate ?? '');
  const [weekendUplift, setWeekendUplift] = useState(policy?.calendarWeekendUpliftPercent ?? '');
  const [tiers, setTiers] = useState(
    Array.isArray(policy?.longStayTiers) && policy.longStayTiers.length
      ? policy.longStayTiers.map(tier => ({
          minNights: tier.minNights,
          discountPercent: tier.discountPercent
        }))
      : []
  );

  // Any edit invalidates the previous save's confirmation. Without this the green
  // "تم الحفظ" sits beside a value that was never saved.
  const touched = () => {
    setFormError(null);
    if (save.isSuccess || save.isError) save.reset();
  };

  const updateTier = (index, field, value) => {
    touched();
    setTiers(current => current.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)));
  };

  // `<input type="number">` reports an EMPTY string while the user is mid-typing something
  // it cannot parse ("2e", a pasted "abc"). Treating that as "cleared" wiped a stored floor
  // to null while the browser still displayed the text — the advisory then stopped firing on
  // every future quote with nothing on screen to explain it. `validity.badInput` is the only
  // way to tell "the user emptied this" from "the browser cannot read this".
  const numericChange = (setter) => (e) => {
    touched();
    if (e.target.validity?.badInput) {
      setFormError(t('pricing.policy.invalidNumber'));
      return;
    }
    setter(e.target.value);
  };

  const submit = (e) => {
    e.preventDefault();
    setFormError(null);

    // A half-filled tier used to be dropped from the payload while staying on screen —
    // the save returned 200, the green tick appeared, and ops walked away believing a
    // 4-night discount existed. Refuse instead of silently discarding.
    const halfFilled = tiers.some(tier =>
      (tier.minNights === '') !== (tier.discountPercent === ''));
    if (halfFilled) {
      setFormError(t('pricing.policy.tierIncomplete'));
      return;
    }

    const numberOrNull = (v) => (v === '' || v === null ? null : Number(v));
    const cleanTiers = tiers
      .filter(tier => tier.minNights !== '' && tier.discountPercent !== '')
      .map(tier => ({
        minNights: Number(tier.minNights),
        discountPercent: Number(tier.discountPercent)
      }));

    // ⚠️ Send ONLY what this form actually changed. The backend leaves an omitted field
    // alone, so a partial payload is safe — whereas sending all three every time makes
    // every save a full overwrite from local state. Any divergence between that state and
    // what is stored then destroys the other fields silently: editing only the floor
    // posted `longStayTiers: []` alongside it and wiped a unit's discounts.
    //
    // Compared against the `policy` prop, which refreshes after each save because the
    // mutation invalidates the guidance query — so the baseline follows the record.
    const sameTiers = (a, b) =>
      a.length === b.length
      && a.every((t, i) => t.minNights === b[i]?.minNights
        && t.discountPercent === b[i]?.discountPercent);

    const payload = {};
    if (numberOrNull(referenceRate) !== (policy?.referenceRate ?? null)) payload.referenceRate = numberOrNull(referenceRate);
    if (numberOrNull(floor) !== (policy?.floor ?? null)) payload.floor = numberOrNull(floor);
    if (numberOrNull(calendarRate) !== (policy?.calendarRate ?? null)) payload.calendarRate = numberOrNull(calendarRate);
    if (numberOrNull(weekendUplift) !== (policy?.calendarWeekendUpliftPercent ?? null)) {
      payload.calendarWeekendUpliftPercent = numberOrNull(weekendUplift);
    }
    if (!sameTiers(cleanTiers, policy?.longStayTiers || [])) payload.longStayTiers = cleanTiers;

    // Nothing to save is not an error, but it must not report success either — a green
    // tick for a write that never happened is how ops learns to distrust the tick.
    if (Object.keys(payload).length === 0) return;

    save.mutate(payload);
  };

  return (
    <section className="pg-block">
      <div className="pg-block__head">
        <h2 className="pg-block__title">
          <i className="bi bi-sliders" />
          {t('pricing.policy.heading')}
        </h2>
        <p className="pg-block__hint">{t('pricing.policy.hint')}</p>
      </div>

      <form onSubmit={submit}>
        <div className="pg-policy__row">
          <div className="pg-policy__field">
            <label htmlFor="pg-reference">{t('pricing.policy.referenceRate')}</label>
            <input
              id="pg-reference"
              type="number"
              min="0"
              className="form-control"
              style={{ textAlign: isArabic ? 'right' : 'left' }}
              value={referenceRate}
              onChange={numericChange(setReferenceRate)}
            />
            <p className="pg-block__hint">{t('pricing.policy.referenceRateHint')}</p>
          </div>
          <div className="pg-policy__field">
            <label htmlFor="pg-floor">{t('pricing.policy.floor')}</label>
            <input
              id="pg-floor"
              type="number"
              min="0"
              className="form-control"
              style={{ textAlign: isArabic ? 'right' : 'left' }}
              value={floor}
              onChange={numericChange(setFloor)}
            />
            <p className="pg-block__hint">{t('pricing.policy.floorHint')}</p>
          </div>
        </div>

        {/* The one setting on this card that reaches the channels — but only on nights the
            nightly horizon job creates; it never re-prices an existing night
            (tasks/fix-availability-horizon-airport.md §9, D3/D6). */}
        <div className="pg-policy__calendar">
          <div className="pg-block__title" style={{ fontSize: '0.9rem' }}>
            {t('pricing.policy.calendarHeading')}
          </div>
          <p className="pg-block__hint">{t('pricing.policy.calendarHint')}</p>
          <div className="pg-policy__row">
            <div className="pg-policy__field">
              <label htmlFor="pg-calendar-rate">{t('pricing.policy.calendarRate')}</label>
              <input
                id="pg-calendar-rate"
                type="number"
                min="1"
                className="form-control"
                style={{ textAlign: isArabic ? 'right' : 'left' }}
                value={calendarRate}
                onChange={numericChange(setCalendarRate)}
              />
              <p className="pg-block__hint">{t('pricing.policy.calendarRateHint')}</p>
            </div>
            <div className="pg-policy__field">
              <label htmlFor="pg-calendar-weekend">{t('pricing.policy.calendarWeekendUplift')}</label>
              <input
                id="pg-calendar-weekend"
                type="number"
                min="0"
                max="100"
                className="form-control"
                style={{ textAlign: isArabic ? 'right' : 'left' }}
                value={weekendUplift}
                onChange={numericChange(setWeekendUplift)}
              />
              <p className="pg-block__hint">{t('pricing.policy.calendarWeekendUpliftHint')}</p>
            </div>
          </div>
          {/* Reads the SAVED value, not the input: the warning is about what the nightly job
              will find, and an unsaved number in the box does not extend anything. */}
          {!(policy?.calendarRate >= 1) && (
            <p className="pg-caveat">{t('pricing.policy.calendarRateMissing')}</p>
          )}
        </div>

        <div className="pg-policy__tiers">
          <div className="pg-block__title" style={{ fontSize: '0.9rem' }}>
            {t('pricing.policy.tiers')}
          </div>
          <p className="pg-block__hint">{t('pricing.policy.tiersHint')}</p>

          {tiers.length === 0 && (
            <p className="pg-table__empty" style={{ fontSize: '0.82rem' }}>
              {t('pricing.policy.noTiers')}
            </p>
          )}

          {tiers.map((tier, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="pg-policy__tier" key={index}>
              <div className="pg-policy__field">
                <label htmlFor={`pg-tier-nights-${index}`}>{t('pricing.policy.minNights')}</label>
                <input
                  id={`pg-tier-nights-${index}`}
                  type="number"
                  min="2"
                  step="1"
                  className="form-control"
                  style={{ textAlign: isArabic ? 'right' : 'left' }}
                  value={tier.minNights}
                  onChange={(e) => updateTier(index, 'minNights', e.target.value)}
                />
              </div>
              <div className="pg-policy__field">
                <label htmlFor={`pg-tier-discount-${index}`}>
                  {t('pricing.policy.discountPercent')}
                </label>
                <input
                  id={`pg-tier-discount-${index}`}
                  type="number"
                  min="0"
                  max="90"
                  className="form-control"
                  style={{ textAlign: isArabic ? 'right' : 'left' }}
                  value={tier.discountPercent}
                  onChange={(e) => updateTier(index, 'discountPercent', e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { touched(); setTiers(current => current.filter((_, i) => i !== index)); }}
              >
                {t('pricing.policy.removeTier')}
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => { touched(); setTiers(current => [...current, { ...emptyTier }]); }}
          >
            <i className="bi bi-plus-lg" style={{ marginInlineEnd: 4 }} />
            {t('pricing.policy.addTier')}
          </button>
        </div>

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            {save.isPending ? t('pricing.policy.saving') : t('pricing.policy.save')}
          </button>
          {save.isSuccess && !formError && (
            <span className="pg-policy__ok">
              <i className="bi bi-check-circle" style={{ marginInlineEnd: 4 }} />
              {t('pricing.policy.saved')}
            </span>
          )}
          {(formError || save.isError) && (
            <span className="pg-policy__error">
              {/* The stable `code`, never the backend's Arabic sentence — that string is
                  one language on a surface that ships two. */}
              {formError || pricingErrorMessage(save.error, t, 'pricing.policy.saveError')}
            </span>
          )}
        </div>
      </form>
    </section>
  );
};

export default PolicySettings;
